/**
 * ColumnFilter — controlled multi-select dropdown for table column headers.
 * Receives selectedValues + onChange from parent (no internal selection state).
 * Option list is virtualized via react-window for performance with high-cardinality columns.
 */
import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Menu,
  Checkbox,
  ListItemText,
  Typography,
  Tooltip,
} from "@material-ui/core";
import {
  Check as CheckIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@material-ui/icons";
import styled from "styled-components/macro";
import { FixedSizeList } from "react-window";

const FilterButton = styled(Box)`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  margin-left: 4px;
  font-size: 0.7rem;
  background-color: ${(props) =>
    props.$active
      ? props.theme.palette.primary.main
      : props.theme.palette.action.hover};
  color: ${(props) =>
    props.$active
      ? props.theme.palette.primary.contrastText
      : props.theme.palette.text.secondary};
  &:hover {
    background-color: ${(props) =>
      props.$active
        ? props.theme.palette.primary.dark
        : props.theme.palette.action.selected};
  }
`;

const SelectOnlyButton = styled.button`
  border: 1px solid ${(props) => props.theme.palette.primary.main};
  border-radius: 3px;
  background: none;
  color: ${(props) => props.theme.palette.primary.main};
  font-size: 0.6rem;
  font-weight: 600;
  cursor: pointer;
  padding: 1px 6px;
  white-space: nowrap;
  margin-left: auto;
  flex-shrink: 0;
  &:hover {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: #fff;
  }
`;

const ITEM_HEIGHT = 32;
const MAX_VISIBLE_ITEMS = 8;

const VirtualRow = React.memo(({ index, style, data }) => {
  const {
    sortedOptions,
    selectedSet,
    handleToggle,
    handleSelectOnly,
    dividerIndex,
  } = data;
  const option = sortedOptions[index];
  const showDivider = index === dividerIndex;
  return (
    <Box
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        paddingLeft: 12,
        paddingRight: 8,
        borderBottom: showDivider ? "1px solid #ccc" : undefined,
      }}
      onClick={() => handleToggle(option)}
      role="menuitem"
    >
      <Checkbox
        checked={selectedSet.has(option)}
        size="small"
        style={{ padding: 2, marginRight: 8, flexShrink: 0 }}
      />
      <ListItemText
        primary={option}
        primaryTypographyProps={{
          variant: "body2",
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      />
      <SelectOnlyButton
        onClick={(e) => {
          e.stopPropagation();
          handleSelectOnly(option);
        }}
      >
        only
      </SelectOnlyButton>
    </Box>
  );
});

const ColumnFilter = ({ options, selectedValues, onChange, label }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
  };

  const handleToggle = useCallback(
    (value) => {
      const newSelected = selectedSet.has(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onChange(newSelected);
    },
    [selectedValues, selectedSet, onChange]
  );

  const handleSelectOnly = useCallback(
    (value) => {
      onChange([value]);
    },
    [onChange]
  );

  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    const lower = searchText.toLowerCase();
    return options.filter((opt) => String(opt).toLowerCase().includes(lower));
  }, [options, searchText]);

  // Bump selected items to the top, preserving order within each group
  const { sortedOptions, dividerIndex } = useMemo(() => {
    const selected = [];
    const unselected = [];
    for (const opt of filteredOptions) {
      if (selectedSet.has(opt)) {
        selected.push(opt);
      } else {
        unselected.push(opt);
      }
    }
    return {
      sortedOptions: [...selected, ...unselected],
      // Index of the last selected item (-1 if none selected or all selected)
      dividerIndex:
        selected.length > 0 && unselected.length > 0 ? selected.length - 1 : -1,
    };
  }, [filteredOptions, selectedSet]);

  const handleSelectAll = () => {
    const newSelected = [...new Set([...selectedValues, ...filteredOptions])];
    onChange(newSelected);
  };

  const handleClearAll = () => {
    if (searchText) {
      const filteredSet = new Set(filteredOptions);
      onChange(selectedValues.filter((v) => !filteredSet.has(v)));
    } else {
      onChange([]);
    }
  };

  const isFiltered = selectedValues.length !== options.length;

  return (
    <>
      <Tooltip title={`Filter ${label}`}>
        <FilterButton $active={isFiltered} onClick={handleClick}>
          <FilterIcon style={{ fontSize: 12 }} />
          <ArrowDropDownIcon style={{ fontSize: 14, marginLeft: -2 }} />
        </FilterButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock
        PaperProps={{
          style: {
            maxHeight: 350,
            minWidth: 280,
            width: 320,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          px={1}
          py={0.5}
          borderBottom={1}
          borderColor="divider"
          style={{
            flexShrink: 0,
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,
          }}
        >
          <TextField
            size="small"
            placeholder={`Search ${label}...`}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
            InputProps={{
              style: { fontSize: "0.75rem" },
            }}
            style={{ marginBottom: 4 }}
          />
          <Box display="flex" justifyContent="space-between">
            <Button
              size="small"
              color="primary"
              startIcon={<CheckIcon style={{ fontSize: 14 }} />}
              onClick={handleSelectAll}
              style={{ fontSize: "0.7rem", padding: "2px 6px", minWidth: 0 }}
            >
              All{searchText ? " visible" : ""}
            </Button>
            <Button
              size="small"
              color="secondary"
              startIcon={<ClearIcon style={{ fontSize: 14 }} />}
              onClick={handleClearAll}
              style={{ fontSize: "0.7rem", padding: "2px 6px", minWidth: 0 }}
            >
              Clear{searchText ? " visible" : ""}
            </Button>
          </Box>
        </Box>
        <Box style={{ flex: 1, minHeight: 0 }}>
          {sortedOptions.length === 0 ? (
            <Box px={2} py={1}>
              <Typography variant="body2" color="textSecondary">
                No matches
              </Typography>
            </Box>
          ) : (
            <FixedSizeList
              height={Math.min(
                sortedOptions.length * ITEM_HEIGHT,
                MAX_VISIBLE_ITEMS * ITEM_HEIGHT
              )}
              width="100%"
              itemSize={ITEM_HEIGHT}
              itemCount={sortedOptions.length}
              itemData={{
                sortedOptions,
                selectedSet,
                handleToggle,
                handleSelectOnly,
                dividerIndex,
              }}
            >
              {VirtualRow}
            </FixedSizeList>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default ColumnFilter;
